/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import axios from 'axios';
import {
    ReadMoreBlock,
    NotFoundPage,
    DisplayHtmlContent,
    Heading,
    Placeholder,
    MediaGallery,
    Accordion,
    DividerNew as Divider,
    Alert,
    SkipLink,
} from 'sarsaparilla';
import { AnchorPageSection } from 'shared-ui/components/AnchorPageWithSidebarLayout';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { SharedVenuePage } from 'shared-ui/components/SharedVenuePage/SharedVenuePage';

import {
    RatingSummaryContainer,
    CellSummaryContainer,
} from 'ui-rating-review/src/containers/AggregateBar';
import { INVENTORY_VEHICLE_PERMIT } from 'ui-field-sales/src/constants/types';
import GettingHereShared from 'ui-permit/shared-components/venuePage/GettingHereShared';
import { ContentTextInfo } from 'ui-permit/shared-components/venuePage/FacilityInfoTabs';
import PermitVenueDates from 'ui-permit/shared-components/venuePage/PermitVenueDates';
import VenueTabWrapper from 'ui-permit/shared-components/venuePage/VenueTabWrapper';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import PermitMap from 'ui-permit/src/shared/components/PermitMap';
import RightRailContainer from 'ui-permit/shared-components/venuePage/RightRail';

import { PERMIT, SEASON } from '../propTypes';
import ClearCreekSidebar from '../components/ClearCreekSidebar';
import CapeHatterasSidebar from '../components/CapeHatterasSidebar';
import CapeLookoutSidebar from '../components/CapeLookoutSidebar';
import FloridaOHVSidebar from '../components/FloridaOHVSidebar';
import AssateagueSidebar from '../components/AssateagueSidebar';
import BigCypressSidebar from '../components/BigCypressSidebar';
import Video from '../components/Video';
import * as permitActions from '../actions/permit';
import {
    CLEAR_CREEK_PERMIT_ID,
    CAPE_HATTERAS_PERMIT_ID,
    FLORIDA_OHV_PERMIT_ID,
    CAPE_LOOKOUT_PERMIT_ID,
    ASSATEAGUE_PERMIT_ID,
    BIG_CYPRESS_PERMIT_ID,
    BIG_CYPRESS_LANDS_PERMIT_ID,
    isInvalidPermitId,
} from '../utils/utils';

export class PermitVenuePage extends React.Component {
    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        error: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    error: PropTypes.string,
                }),
            ])
        ),
        fetchError: PropTypes.shape({
            error: PropTypes.string,
        }),
        pActions: PropTypes.object.isRequired,
        permit: PERMIT,
        seasons: PropTypes.arrayOf(SEASON),
        recarea: PropTypes.arrayOf(PropTypes.object),
        recareaError: PropTypes.string,
        account: PropTypes.object,
        ratingReviews: PropTypes.object,
    };

    static defaultProps = {
        router: { params: {} },
        permit: {
            links: [],
            name: '',
        },
        recarea: [],
    };

    static displayName = 'PermitVenuePage';

    constructor(props) {
        super(props);
        this.state = {
            permitId: props.router.params.permitId,
            isLoadingRailContent: true,
        };
        this.handleDivisionSelection = this.handleDivisionSelection.bind(this);
    }

    componentDidMount() {
        const permitId = this.state.permitId;

        this.props.pActions.fetchPermit(permitId).then((permit) => {
            const recAreaId = get(permit, 'recarea');
            const recAreaName = get(this.props, 'recarea.[0].name');

            if (recAreaId && !recAreaName) {
                this.props.pActions.fetchRecarea(recAreaId);
            }
        });

        this.props.pActions.fetchSeasons(permitId).then(() => {
            this.setState({ isLoadingRailContent: false });
        });

        this.getMapData(permitId);
    }

    getMapData(permitId) {
        axios
            .get(`/shared/vehicle-permit/data/${permitId}/features.json`, {
                credentials: 'include',
            })
            .then(({ data }) => {
                this.setState({
                    lines: data.lines,
                    points: data.points,
                    polygons: data.polygons,
                });
            })
            .catch(() => {});
    }

    getBreadcrumbsPath = () => {
        const { permit, recarea } = this.props;
        const recAreaName = recarea?.[0]?.name;
        const recAreaId = recarea?.[0]?.entity_id;
        const gatewaysURL = getGatewayUrl(recAreaId);
        const path = [{ title: 'Home', url: '/' }];
        if (recAreaName && recAreaId) {
            path.push({ title: recAreaName, url: gatewaysURL });
        }
        path.push({ title: permit.name });
        return path;
    };

    // handles the event of changing the selected division in the availability widget
    handleDivisionSelection(divisionId) {
        this.execFetchAvailability(this.props.permit.id, divisionId);
    }

    tabsToShow(isLoading) {
        if (isLoading) return [];
        const { latitude, longitude } = this.props.permit;
        const { driving_directions: drivingDirections } = this.props.permit.description;

        return [
            {
                label: 'Overview',
                content: this.renderOverview(),
            },
            {
                label: 'Need to Know',
                content: this.renderNeedToKnow(),
            },
            {
                label: 'Fees & Cancellations',
                content: this.renderCancellation(),
            },
            {
                label: 'Getting Here',
                content: (
                    <GettingHereShared
                        permitId={this.state.permitId}
                        longitude={longitude}
                        latitude={latitude}
                        drivingDirections={drivingDirections}
                        isVehicle
                    />
                ),
            },
            {
                label: 'Contacts',
                content: this.renderContacts(),
            },
        ];
    }

    renderOverview() {
        const {
            description,
            season_information: seasonInformation,
            edu_video_url: eduVideoURL,
            edu_video_title: eduVideoTitle,
            video_url: videoURL,
            video_title: videoTitle,
            video_transcript: videoTranscript,
            edu_video_transcript: eduVideoTranscript,
        } = this.props.permit.description;

        return (
            <VenueTabWrapper permitId={this.state.permitId} isVehicle>
                <div className="content-section">
                    <ReadMoreBlock linesToShow={5}>
                        <DisplayHtmlContent html={description} />
                    </ReadMoreBlock>
                </div>

                {seasonInformation && (
                    <ContentTextInfo
                        heading="Permit & Season Information"
                        textContent={seasonInformation}
                    />
                )}

                {this.props.permit?.important_dates && (
                    <>
                        <AnchorPageSection heading="Important Dates">
                            <PermitVenueDates permit={this.props.permit} hideTitle />
                        </AnchorPageSection>
                        <Divider />
                    </>
                )}

                {this.renderQuotaDetails()}

                {videoURL && (
                    <>
                        <Video videoTitle={videoTitle} url={videoURL} />
                        {videoTranscript && (
                            <Accordion
                                id="videoTranscript"
                                items={[
                                    {
                                        heading: 'Video Transcript',
                                        content: (
                                            <DisplayHtmlContent html={videoTranscript} />
                                        ),
                                    },
                                ]}
                            />
                        )}
                    </>
                )}
                {eduVideoURL && (
                    <>
                        <Video title={eduVideoTitle} url={eduVideoURL} />
                        {eduVideoTranscript && (
                            <Accordion
                                id="eduVideoTranscript"
                                items={[
                                    {
                                        heading: 'Video Transcript',
                                        content: (
                                            <DisplayHtmlContent
                                                html={eduVideoTranscript}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        )}
                    </>
                )}

                <Divider />
                <SkipLink href="#sidebar-anchor" position="above">
                    Skip to Right Sidebar
                </SkipLink>
                <MediaGallery assetId={this.state.permitId} />
            </VenueTabWrapper>
        );
    }

    renderQuotaDetails() {
        const { quota_details: quotaDetails } = this.props.permit.description;

        if (quotaDetails) {
            return (
                <ContentTextInfo
                    heading="How does the quota work for this permit?"
                    textContent={quotaDetails}
                />
            );
        }
        return null;
    }

    renderNeedToKnow() {
        const { need_to_know_permit: needToKnowPermit } = this.props.permit.description;

        return (
            <VenueTabWrapper permitId={this.state.permitId} isVehicle>
                {this.renderContent(needToKnowPermit)}
            </VenueTabWrapper>
        );
    }

    renderContent(body, title) {
        if (body) {
            return (
                <>
                    {title && (
                        <Heading appearance="h5" headingLevel={2}>
                            {title}
                        </Heading>
                    )}
                    <DisplayHtmlContent html={body} />
                </>
            );
        }
        return null;
    }

    renderCancellation() {
        const {
            fee_policy: feePolicy,
            cancellation_policy: cancellationPolicy,
            no_show_policy: noShowPolicy,
            discounts,
        } = this.props.permit.description;

        return (
            <VenueTabWrapper permitId={this.state.permitId} isVehicle>
                {this.renderContent(feePolicy, 'Fee Policies')}
                {this.renderContent(cancellationPolicy, 'Cancellation & Change Policies')}
                {this.renderContent(noShowPolicy, 'No Show Policies')}
                {this.renderContent(discounts, 'Discount Pass Policies')}
            </VenueTabWrapper>
        );
    }

    renderContacts() {
        const description1 = get(this.props, 'permit.addresses[0].description1');
        const description2 = get(this.props, 'permit.addresses[0].description2');
        const address1 = get(this.props, 'permit.addresses[0].address1');
        const address2 = get(this.props, 'permit.addresses[0].address2');
        const address3parts = [
            get(this.props, 'permit.addresses[0].city'),
            get(this.props, 'permit.addresses[0].state'),
            get(this.props, 'permit.addresses[0].zip_code'),
        ];
        let address3;
        if (address3parts[0] && address3parts[1] && address3parts[2]) {
            address3 = `${address3parts[0]}, ${address3parts[1]} ${address3parts[2]}`;
        }
        const remarks = get(this.props, 'permit.addresses[0].remarks');
        return (
            <VenueTabWrapper permitId={this.state.permitId} isVehicle>
                {this.props.permit.addresses && (
                    <div>
                        <div className="permit-venue-row">
                            <div className="permit-venue-left-col">
                                <Heading
                                    headingLevel={2}
                                    appearance="h5"
                                    className="mb-2"
                                >
                                    Mailing Address
                                </Heading>
                            </div>
                            <div className="permit-venue-right-col">
                                <div translate="no">
                                    {description1 && <div>{description1}</div>}
                                    {description2 && <div>{description2}</div>}
                                    {address1 && <div>{address1}</div>}
                                    {address2 && <div>{address2}</div>}
                                    {address3 && <div>{address3}</div>}
                                </div>
                                {remarks && <div>{remarks}</div>}
                            </div>
                        </div>
                        <div className="permit-venue-row">
                            <div className="permit-venue-left-col">
                                <Heading
                                    headingLevel={2}
                                    appearance="h5"
                                    className="mb-2"
                                >
                                    Phone Number
                                </Heading>
                            </div>
                            <div className="permit-venue-right-col">
                                <p>{this.props.permit.phone}</p>
                            </div>
                        </div>
                    </div>
                )}
            </VenueTabWrapper>
        );
    }

    renderRightRailBody() {
        const { permit, seasons, pActions } = this.props;

        switch (this.state.permitId) {
            case CLEAR_CREEK_PERMIT_ID:
                return (
                    <ClearCreekSidebar
                        key="ClearCreek"
                        permit={permit}
                        pActions={pActions}
                        seasons={seasons}
                        account={this.props.account}
                    />
                );
            case CAPE_HATTERAS_PERMIT_ID:
                return (
                    <CapeHatterasSidebar
                        key="CapeHatteras"
                        permit={permit}
                        seasons={seasons}
                        pActions={pActions}
                        account={this.props.account}
                    />
                );
            case CAPE_LOOKOUT_PERMIT_ID:
                return (
                    <CapeLookoutSidebar
                        key="CapeLookout"
                        permit={permit}
                        seasons={seasons}
                        pActions={pActions}
                        account={this.props.account}
                    />
                );
            case FLORIDA_OHV_PERMIT_ID:
                return (
                    <FloridaOHVSidebar
                        key="FloridaOHV"
                        permit={permit}
                        seasons={seasons}
                        pActions={pActions}
                        account={this.props.account}
                    />
                );
            case ASSATEAGUE_PERMIT_ID:
                return (
                    <AssateagueSidebar
                        key="Assateague"
                        permit={permit}
                        seasons={seasons}
                        pActions={pActions}
                    />
                );
            case BIG_CYPRESS_PERMIT_ID:
                return (
                    <BigCypressSidebar
                        key="BigCypress"
                        permit={permit}
                        seasons={seasons}
                        pActions={pActions}
                    />
                );
            case BIG_CYPRESS_LANDS_PERMIT_ID:
                return (
                    <BigCypressSidebar
                        key="BigCypressLands"
                        permit={permit}
                        seasons={seasons}
                        pActions={pActions}
                    />
                );
            default:
                return null;
        }
    }

    renderRightRail() {
        const { permit } = this.props;
        const rightRailBody = this.renderRightRailBody();
        return RightRailContainer({
            permit,
            isClosed: !rightRailBody,
            isLoading: this.state.isLoadingRailContent,
            children: [rightRailBody],
        });
    }

    renderMap = (isLoading) => {
        const { longitude, latitude, name } = this.props.permit;

        if (isLoading) {
            return <Placeholder width="100%" height="600px" />;
        }

        return (
            <div id="Mapbox" className="map">
                <PermitMap
                    geoJsonCenterPoint={{
                        geometry: {
                            coordinates: [longitude, latitude],
                            type: 'Point',
                        },
                        properties: {
                            name,
                            type: 'Permit',
                        },
                        type: 'Feature',
                    }}
                    geoJsonLines={this.state.lines}
                    geoJsonPoints={this.state.points}
                    geoJsonPolygons={this.state.polygons}
                    mapContainer="Mapbox"
                />
            </div>
        );
    };

    render() {
        const {
            router: {
                params: { permitId },
            },
            permit,
            error,
            recareaError,
            fetchError,
            recarea,
        } = this.props;

        if (isInvalidPermitId(permitId)) {
            return (
                <NotFoundPage
                    errorBodyText={`Whoops! We could not find the permit you've requested. Please click the button below to go back to the previous page or enter a new search above.`}
                />
            );
        }

        if (error || fetchError) {
            return (
                <div className="permit-content-outer-wrap">
                    <div className="permit-content-wrap m-4" aria-live="polite">
                        <Alert type="error" heading="An error occurred" hasAriaLive />
                    </div>
                </div>
            );
        }

        const isLoading = !permit || this.props.router.params.permitId !== permit.id;
        const recareaId = permit.recarea;
        const permitRecareaName = permit?.recarea_name
            ? permit?.recarea_name
            : permit?.name;
        const recareaName =
            recarea && recarea.length > 0 ? recarea?.[0]?.name : permitRecareaName;

        const partOf = recareaName
            ? { title: recareaName, url: recareaId ? getGatewayUrl(recareaId) : null }
            : null;

        const ratingParams = { locationType: 'Vehicle Permit', locationId: permit.id };
        const ratingsPresent =
            get(this.props.ratingReviews, 'number_of_ratings', 0) !== 0;
        const cellCoveragePresent = !isNil(
            get(this.props.ratingReviews, 'aggregate_cell_coverage_ratings')
        );
        return (
            <div className="venue-page">
                {/* Fire the view_item GA Event */}
                <SendGAEvent
                    itemLevel="Facility"
                    itemCategory={INVENTORY_VEHICLE_PERMIT}
                    facilityId={this.props.permit.id}
                    facilityName={this.props.permit.name}
                    isDataLoading={!this.props.permit || !this.props.permit.id}
                />
                <SharedVenuePage
                    isLoading={isLoading}
                    errors={[recareaError].filter((err) => err)}
                    title={permit.name || ''}
                    rightRail={this.renderRightRail()}
                    assetId={permitId}
                    favoriteEntityId={permitId}
                    favoriteEntityType={'vehiclepermit'}
                    inventoryType="Permit"
                    breadcrumbPath={this.getBreadcrumbsPath()}
                    partOf={partOf}
                    mapLatitude={permit.latitude}
                    mapLongitude={permit.longitude}
                    map={this.renderMap(isLoading)}
                    tabs={this.tabsToShow(isLoading)}
                    starRating={
                        ratingsPresent && <RatingSummaryContainer {...ratingParams} />
                    }
                    cellRating={
                        cellCoveragePresent && <CellSummaryContainer {...ratingParams} />
                    }
                />
            </div>
        );
    }
}

export default withCompatRouter(
    connect(
        (state) => {
            return {
                account: state.auth.account,
                activeLottery: state.vehiclepermit.permit.lottery,
                error: state.vehiclepermit.permit.error,
                ld: state.ld,
                navigation: state.navigation,
                permit: state.vehiclepermit.permit.result,
                seasons: state.vehiclepermit.season.data,
                recarea: state.vehiclepermit.permit.recarea,
                recareaError: state.vehiclepermit.permit.recarea_err,
                fetchError: state.vehiclepermit.permit.fetch_err,
                ratingReviews: state.ratingReviews.fetchAggregate.data,
            };
        },
        (dispatch) => {
            return {
                pActions: bindActionCreators(permitActions, dispatch),
            };
        }
    )(PermitVenuePage)
);

// cSpell:ignore recarea, assateague, mapbox, vehiclepermit
